import { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LineElement, PointElement,
  Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { api } from '../api';
import { formatPts, timeAgo, CLAN_COLORS } from '../utils';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

export default function Top50() {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('');
  const [selected, setSelected] = useState(new Set());
  const [trend, setTrend]     = useState(null);
  const [loadingTrend, setLoadingTrend] = useState(false);

  useEffect(() => {
    api.top50()
      .then(setData)
      .catch(e => setError(e.message));
  }, []);

  async function loadTrend() {
    if (!selected.size) return;
    setLoadingTrend(true);
    try {
      const d = await api.top50Trend([...selected].join(','), 48);
      setTrend(d.trends);
    } catch {}
    setLoadingTrend(false);
  }

  function toggleSelect(name) {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : (s.size < 5 && s.add(name));
      return s;
    });
    setTrend(null);
  }

  if (error) return <div className="page"><div className="error-box">{error}</div></div>;
  if (!data) return <div className="page-center"><div className="spinner" /></div>;

  const q = filter.toLowerCase();
  const list = data.clans.filter(c => !q || c.clanName.toLowerCase().includes(q));

  // Build trend chart
  let trendChart = null;
  if (trend && Object.keys(trend).length > 0) {
    const clans = Object.keys(trend);
    const allTimes = [...new Set(clans.flatMap(c => trend[c].map(p => p.time)))].sort();
    trendChart = {
      labels: allTimes.map(t => {
        const d = new Date(t);
        return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:00`;
      }),
      datasets: clans.map((clan, i) => {
        const map = Object.fromEntries(trend[clan].map(p => [p.time, p.rank]));
        return {
          label: clan,
          data: allTimes.map(t => map[t] ?? null),
          borderColor: CLAN_COLORS[i % CLAN_COLORS.length],
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        };
      }),
    };
  }

  const trendOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { color: '#7d8590', font: { size: 11 } } },
      tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: Rank #${ctx.raw}` } },
    },
    scales: {
      x: { ticks: { color: '#7d8590', maxRotation: 45, font: { size: 10 } }, grid: { color: '#30363d' } },
      y: {
        reverse: true,
        ticks: { color: '#7d8590', callback: v => `#${v}` },
        grid: { color: '#30363d' },
        title: { display: true, text: 'Rank', color: '#7d8590' },
      },
    },
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <div className="page-header-title">Global Top 50</div>
          {data.updatedAt && (
            <div className="page-header-sub">Updated {timeAgo(data.updatedAt)}</div>
          )}
        </div>
      </div>

      {/* Trend section */}
      {selected.size > 0 && (
        <div className="card mb-20">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="chart-title" style={{ margin: 0 }}>
              Rank Trend — {[...selected].join(', ')}
            </div>
            <button className="btn btn-primary" onClick={loadTrend} disabled={loadingTrend}>
              {loadingTrend ? 'Loading…' : 'Load 48h Trend'}
            </button>
          </div>
          {!trend && !loadingTrend && (
            <div className="empty-msg" style={{ padding: '12px 0' }}>Select up to 5 clans and click "Load 48h Trend"</div>
          )}
          {loadingTrend && <div className="page-center" style={{ padding: 20 }}><div className="spinner spinner-sm" /></div>}
          {trendChart && (
            <div className="chart-wrap" style={{ height: 280 }}>
              <Line data={trendChart} options={trendOpts} />
            </div>
          )}
        </div>
      )}

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Filter clans…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        {selected.size > 0 && (
          <span className="badge badge-blue">{selected.size} selected for trend</span>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Clan</th>
              <th>Points</th>
              <th>Change</th>
              <th>Trend</th>
            </tr>
          </thead>
          <tbody>
            {list.map(c => {
              const rankClass = c.rank <= 3 ? `rank-${c.rank}` : '';
              const isSelected = selected.has(c.clanName);
              return (
                <tr key={c.id || c.clanName} className={rankClass}>
                  <td><span className="rank-num">{c.rank}</span></td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{c.clanName}</span>
                    <a
                      href={`https://biggamesapi.io/clan/${encodeURIComponent(c.clanName)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: 8, color: 'var(--muted)', fontSize: '.78rem' }}
                      onClick={e => e.stopPropagation()}
                    >↗</a>
                  </td>
                  <td style={{ fontWeight: 700 }}>{formatPts(c.points)}</td>
                  <td>
                    {c.rankChange === null ? (
                      <span className="rank-change rank-same">—</span>
                    ) : c.rankChange > 0 ? (
                      <span className="rank-change rank-up">▲ {c.rankChange}</span>
                    ) : c.rankChange < 0 ? (
                      <span className="rank-change rank-down">▼ {Math.abs(c.rankChange)}</span>
                    ) : (
                      <span className="rank-change rank-same">— 0</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={`btn${isSelected ? ' btn-primary' : ''}`}
                      style={{ padding: '4px 10px', fontSize: '.75rem' }}
                      onClick={() => toggleSelect(c.clanName)}
                    >
                      {isSelected ? '✓ Tracking' : '+ Track'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {list.length === 0 && <div className="empty-msg">No clans match.</div>}
      </div>

      {data.clans.length === 0 && (
        <div className="empty-msg">No Top 50 snapshot data available yet.</div>
      )}
    </div>
  );
}
